import {
    api,
    ApiError,
    apiErrorMessage,
    browserQuery,
    codeLabel,
    formatMoney,
    oneSpec,
    paymentSystemLabel,
    useFreshQuery,
    useZodForm,
    zodCents,
} from '@bemor/web-kit';
import {
    Button,
    Card,
    CardContent,
    CardHeader,
    CardTitle,
    Checkbox,
    EmptyState,
    Field,
    SandboxIcon as FlaskConical,
    Input,
    MoneyInput,
    PageHeader,
    PublicId,
    SearchableSelect,
    Textarea,
    type SelectOption,
} from '@bemor/web-kit/ui';
import { useState, type FormEvent } from 'react';
import { useFieldArray } from 'react-hook-form';
import { Link, useSearchParams } from 'react-router-dom';
import { z } from 'zod';

import type {
    CheckoutSession,
    ConsoleAllowListEntry,
    ConsoleSessionResult,
    ConsoleSettings,
    PaymentSystemOffer,
} from '@/api/types';
import { SearchError, useMerchantSearch } from '@/lib/merchant-search';
import { offerText } from '@/lib/offer';
import { useOne, useWrite } from '@/lib/queries';

const PAGE = '/preview-checkout';
const MISMATCH = 'console_confirmation_mismatch';

const KINDS: SelectOption[] = [
    { value: 'one_off', label: 'One-off' },
    { value: 'recurring', label: 'Recurring' },
];
// Catalogue Plan::INTERVALS: week, month or year
const INTERVALS: SelectOption[] = [
    { value: 'week', label: 'Week' },
    { value: 'month', label: 'Month' },
    { value: 'year', label: 'Year' },
];

const QUANTITY = /^[1-9]\d*$/;
const line = z.object({
    name: z
        .string()
        .trim()
        .min(1, 'Name the line')
        .max(200, 'At most 200 characters'),
    // BM-46 §2.7: integer cents from the money input
    amount: zodCents({ min: 1 }),
    quantity: z.string().regex(QUANTITY, 'Enter a whole number from 1'),
});
const NEW_LINE = {
    name: '',
    amount: null as unknown as number,
    quantity: '1',
};

const schema = z.object({
    kind: z.enum(['one_off', 'recurring']),
    // a One-off order takes any number of lines; a plan is bought alone (SessionService::validatedLines mode_conflict)
    lines: z.array(line).min(1),
    interval: z.enum(['week', 'month', 'year']),
    trial_days: z.string().regex(/^\d*$/, 'Enter a whole number of days'),
    trial_price: z.number().int().min(0).nullable(),
    save_card: z.boolean(),
    // V-5: optional, blank = not sent; the API validates them (CheckoutSession::merchantText) and answers a 422 field error
    customer_ref: z.string(),
    reference: z.string(),
    reason: z
        .string()
        .trim()
        .min(1, 'Give a short reason')
        .max(255, 'At most 255 characters'),
    confirm: z.number().int().nullable(),
});
type Values = z.infer<typeof schema>;

/**
 * The order total as the API computes it for the confirm check (SessionService::confirmedConsoleAmount): the sum of
 * unit amount times quantity over the lines it is sent. Display only; the API compares the typed amount, never this.
 */
function orderTotal(lines: Values['lines'], recurring: boolean): number {
    return lines
        .slice(0, recurring ? 1 : undefined)
        .reduce(
            (sum, l) =>
                sum +
                (l.amount ?? 0) *
                    (recurring
                        ? 1
                        : QUANTITY.test(l.quantity)
                          ? Number(l.quantity)
                          : 0),
            0,
        );
}

/**
 * The console session body (§ 12): one inline price_data line per order line (BM-47). A blank customer_ref is left out
 * so the API generates its console- one; a blank reference is left out and the session has none (V-5). The Buyer
 * country is the session's network country (bm-111 § 19); without a valid code no key is sent and the header decides.
 */
function sessionBody(
    merchantId: string,
    v: Values,
    confirm: boolean,
    country: string | null,
) {
    const recurring = v.kind === 'recurring';
    const customerRef = v.customer_ref.trim();
    const reference = v.reference.trim();
    return {
        merchant_id: merchantId,
        reason: v.reason,
        ...(customerRef ? { customer_ref: customerRef } : {}),
        ...(reference ? { reference } : {}),
        ...(country ? { client_country: country } : {}),
        // a plan is its one line (the form keeps only lines[0] for it)
        lines: v.lines.slice(0, recurring ? 1 : undefined).map((l) => ({
            // a plan line is quantity 1 (SessionService::assertPlanQuantity)
            quantity: recurring ? 1 : Number(l.quantity),
            price_data: {
                currency: 'USD',
                amount_cents: l.amount,
                product_data: { name: l.name },
                ...(recurring
                    ? {
                          recurring: {
                              interval: v.interval,
                              interval_count: 1,
                              trial_days: Number(v.trial_days || 0),
                              trial_price_cents: v.trial_price ?? 0,
                          },
                      }
                    : {}),
            },
        })),
        // a one-off order offers the buyer the saved-card consent only when asked; a plan always serves it (SessionService)
        ...(!recurring && v.save_card ? { save_payment_method: true } : {}),
        ...(confirm && v.confirm !== null
            ? { confirm_amount_cents: v.confirm }
            : {}),
    };
}

/**
 * BM-70 § 13 (T-42): staff open checkout for a Merchant on their own console allow-list. Before submit the page shows
 * the offer diagnosis (§ 10); submit creates the console session (§ 12) and opens the hosted pay page in a new tab
 * (the console stays put and follows the session below the form until it settles), which returns to this page with
 * `?session=` in that tab; both show the resulting ids as plain text. Refund and void of a console charge
 * use the charge page.
 */
export function TestConsolePage() {
    const [params] = useSearchParams();
    const session = params.get('session');
    return (
        <div className="space-y-4">
            <PageHeader
                title="Preview checkout"
                meta="Open checkout for a Merchant on your preview checkout allow-list."
                primaryAction={
                    session ? (
                        <Button asChild variant="secondary">
                            <Link to={PAGE}>New test order</Link>
                        </Button>
                    ) : null
                }
            />
            {session ? <ConsoleResult sessionId={session} /> : <ConsoleOrder />}
            <TestCards />
        </div>
    );
}

// V-2: the NMI sandbox values, from dev/ai-docs/bm-9-basic-modules/04-nmi-integration.md:115-118 (duplicates :64)
const CARDS: [string, string][] = [
    ['Visa', '4111111111111111'],
    ['Mastercard', '5431111111111111'],
    ['Amex', '341111111111111'],
    ['3-D Secure, frictionless', '4100000000000100'],
    ['3-D Secure, challenge (code 12345)', '4100000000005000'],
];
const OUTCOMES: [string, string][] = [
    ['CVV 999', 'Security code matches.'],
    [
        'Any other CVV',
        'Security code mismatch: Bemor voids the hold and the buyer sees "Security code mismatch".',
    ],
    ['Amount below $1.00', 'Declined.'],
    ['Address 888 with ZIP 77777', 'Address (AVS) matches.'],
    [
        'Same card and amount again soon after',
        'NMI refuses it as a duplicate for a while: use another amount or card.',
    ],
];

/**
 * V-2: which test value produces which outcome, so staff can drive each case on the pay page. The API decides whether
 * it shows: it names its environment only when it is local or staging, and production answers null, so nothing
 * renders there (nor while the read is loading or failed). The admin's own build env never decides.
 */
function TestCards() {
    const query = useOne<ConsoleSettings>('/staff/console/settings');
    const env = query.data?.data.test_environment;
    if (!env) return null;
    return (
        <Card data-testid="console-test-cards">
            <CardHeader>
                <CardTitle>NMI test cards</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 text-sm">
                <dl className="grid grid-cols-1 gap-x-6 gap-y-2 md:grid-cols-[auto_1fr]">
                    {CARDS.map(([label, number]) => (
                        <div key={number} className="contents">
                            <dt className="text-muted-foreground">{label}</dt>
                            <dd className="min-w-0">
                                <PublicId id={number} short={false} />
                            </dd>
                        </div>
                    ))}
                    <dt className="text-muted-foreground">Expiry</dt>
                    <dd>10/29</dd>
                    {OUTCOMES.map(([value, outcome]) => (
                        <div key={value} className="contents">
                            <dt className="text-muted-foreground">{value}</dt>
                            <dd>{outcome}</dd>
                        </div>
                    ))}
                    {env === 'local' ? (
                        <>
                            <dt className="text-muted-foreground">
                                Tax (local only)
                            </dt>
                            <dd>
                                The local stack has no Anrok key: tax is quoted
                                only where a rate is stored for the ZIP, or from
                                the seed where
                                checkout.tax_seed_fallback.enabled is on; any
                                other ZIP answers Tax could not be calculated.
                            </dd>
                        </>
                    ) : null}
                </dl>
            </CardContent>
        </Card>
    );
}

function ConsoleOrder() {
    const allowList = useOne<ConsoleAllowListEntry[]>(
        '/staff/console/allow-list',
    );
    const [merchant, setMerchant] = useState<string | null>(null);
    const [country, setCountry] = useState('');
    if (allowList.isError)
        return (
            <p role="alert" className="text-destructive text-sm">
                The preview checkout allow-list did not load:{' '}
                {apiErrorMessage(allowList.error)}
            </p>
        );
    const entries = allowList.data?.data;
    if (!entries) return <p className="text-muted-foreground text-sm">…</p>;
    if (entries.length === 0) return <AllowList entries={entries} />;
    // ponytail: the allow-list answers merchant ids only, so the picker shows ids; add names if staff ask for them
    const options = entries.map((e) => ({
        value: e.merchant_id,
        label: e.merchant_id,
    }));
    // a Merchant taken off the list leaves the order picker and its open order (D-58)
    const chosen = options.some((o) => o.value === merchant) ? merchant : null;
    const code = country.trim().toUpperCase();
    const buyerCountry = /^[A-Z]{2}$/.test(code) ? code : null;
    return (
        <div className="space-y-4">
            <AllowList entries={entries} />
            <Card>
                <CardContent className="grid grid-cols-1 gap-4 pt-6 md:grid-cols-2">
                    <Field id="merchant" label="Merchant">
                        <SearchableSelect
                            id="merchant"
                            label="Merchant"
                            placeholder="Choose a Merchant"
                            options={options}
                            value={chosen}
                            onChange={setMerchant}
                            testId="console-merchant"
                        />
                    </Field>
                    <Field
                        id="country"
                        label="Buyer country"
                        hint="Optional: a two-letter code the diagnosis applies and checkout takes as the buyer's country."
                    >
                        <Input
                            id="country"
                            maxLength={2}
                            value={country}
                            onChange={(e) => setCountry(e.target.value)}
                        />
                    </Field>
                </CardContent>
            </Card>
            {chosen ? (
                <>
                    <Diagnosis merchant={chosen} country={buyerCountry} />
                    <OrderForm
                        key={chosen}
                        merchant={chosen}
                        country={buyerCountry}
                    />
                </>
            ) : null}
        </div>
    );
}

const ALLOW_LIST = '/staff/console/allow-list';

/**
 * The caller's own console allow-list (BM-70 § 13, D-58): each entry with Remove, and an Add over the Merchants of the
 * caller's scope (the staff merchants read). Never sends a staff_id: another staff member's list stays API-only (§ 12).
 * A write refreshes the list, and with it the order picker; a refused write shows the API message and changes nothing.
 */
function AllowList({ entries }: { entries: readonly ConsoleAllowListEntry[] }) {
    const search = useMerchantSearch();
    const [picked, setPicked] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);
    const add = useWrite(
        (merchant: string) =>
            api().put(`${ALLOW_LIST}/${encodeURIComponent(merchant)}`),
        [ALLOW_LIST],
    );
    const remove = useWrite(
        (merchant: string) =>
            api().delete(`${ALLOW_LIST}/${encodeURIComponent(merchant)}`),
        [ALLOW_LIST],
    );
    const busy = add.isPending || remove.isPending;
    const onError = (e: unknown) => setError(apiErrorMessage(e));
    return (
        <Card data-testid="console-allow-list">
            <CardHeader>
                <CardTitle>Your allow-list</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
                {entries.length === 0 ? (
                    <EmptyState
                        icon={FlaskConical}
                        title="No Merchant is on your preview checkout allow-list yet."
                        body="Preview checkout opens checkout only for a Merchant on your own allow-list."
                    />
                ) : (
                    <ul className="divide-border-1 divide-y text-sm">
                        {entries.map((e) => (
                            <li
                                key={e.merchant_id}
                                className="flex items-center justify-between gap-4 py-2"
                            >
                                <span className="min-w-0 font-mono break-all">
                                    {e.merchant_id}
                                </span>
                                <Button
                                    type="button"
                                    variant="secondary"
                                    aria-label={`Remove ${e.merchant_id}`}
                                    disabled={busy}
                                    onClick={() => {
                                        setError(null);
                                        remove.mutate(e.merchant_id, {
                                            onError,
                                        });
                                    }}
                                >
                                    Remove
                                </Button>
                            </li>
                        ))}
                    </ul>
                )}
                <form
                    method="post"
                    className="flex flex-col gap-2 md:flex-row md:items-end"
                    onSubmit={(e: FormEvent) => {
                        e.preventDefault();
                        if (!picked) return;
                        setError(null);
                        add.mutate(picked, {
                            onSuccess: () => setPicked(null),
                            onError,
                        });
                    }}
                >
                    <Field id="console-allow-add" label="Add a Merchant">
                        <SearchableSelect
                            id="console-allow-add"
                            label="Merchant"
                            placeholder="Choose a Merchant"
                            loadOptions={search.load}
                            value={picked}
                            onChange={setPicked}
                            testId="console-allow-add"
                        />
                    </Field>
                    <Button type="submit" disabled={!picked || busy}>
                        Add
                    </Button>
                </form>
                <SearchError error={search.error} />
                {error ? (
                    <p role="alert" className="text-destructive text-sm">
                        {error}
                    </p>
                ) : null}
            </CardContent>
        </Card>
    );
}

/** The offer rule's answer per payment system (§ 10): offered, else the reason it is not. */
function Diagnosis({
    merchant,
    country,
}: {
    merchant: string;
    country: string | null;
}) {
    const query = useOne<PaymentSystemOffer[]>(
        `/staff/payment-systems/merchants/${encodeURIComponent(merchant)}/offer`,
        // the console session stores the typed country as its network country (bm-111 § 19), so the
        // diagnosis applies it to both arms of the offer rule; blank, checkout takes it from the header (§ 18)
        country ? { country, network_country: country } : {},
    );
    return (
        <Card data-testid="console-diagnosis">
            <CardHeader>
                <CardTitle>Payment systems checkout would offer</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
                {country ? null : (
                    <p
                        className="text-muted-foreground text-sm"
                        data-testid="console-diagnosis-network"
                    >
                        The network country is not checked: with no Buyer
                        country, checkout takes it from the buyer&apos;s
                        connection and may refuse a system limited to certain
                        countries.
                    </p>
                )}
                {query.isError ? (
                    <p role="alert" className="text-destructive text-sm">
                        The diagnosis did not load:{' '}
                        {apiErrorMessage(query.error)}
                    </p>
                ) : !query.data ? (
                    <p className="text-muted-foreground text-sm">…</p>
                ) : (
                    <ul className="space-y-1 text-sm">
                        {query.data.data.map((o) => (
                            <li
                                key={o.payment_system}
                                data-testid={`offer-${o.payment_system}`}
                            >
                                {paymentSystemLabel(o.payment_system)}:{' '}
                                {offerText(o)}
                            </li>
                        ))}
                    </ul>
                )}
            </CardContent>
        </Card>
    );
}

/**
 * Open the pay page in a new tab and leave the console where it is; false when a popup blocker refused the tab.
 * Not `window.open(url, '_blank', 'noopener')`: that form always returns null, so a blocked tab could not be told
 * apart from an opened one. The blank tab is still same-origin here, so its opener can be cut before it navigates.
 */
function openTab(url: string): boolean {
    const tab = window.open('', '_blank');
    if (!tab) return false;
    tab.opener = null;
    tab.location.href = url;
    return true;
}

function OrderForm({
    merchant,
    country,
}: {
    merchant: string;
    country: string | null;
}) {
    const [error, setError] = useState<string | null>(null);
    // the API asks for the amount again at or above its threshold (D-8); the threshold is server config
    const [needsConfirm, setNeedsConfirm] = useState(false);
    // one Idempotency-Key per attempt: kept for a resend after no response, replaced after any answer
    const [key, setKey] = useState(() => crypto.randomUUID());
    // the pay page opened in its own tab, or a blocker refused the tab and staff follow the link instead
    const [opened, setOpened] = useState<{
        session: string;
        url: string;
        blocked: boolean;
    } | null>(null);
    const form = useZodForm(schema, {
        defaultValues: {
            kind: 'one_off',
            lines: [{ ...NEW_LINE, name: 'Preview checkout order' }],
            interval: 'month',
            trial_days: '',
            trial_price: null,
            save_card: false,
            customer_ref: '',
            reference: '',
            reason: '',
            confirm: null,
        },
    });
    const create = useWrite(
        async (body: ReturnType<typeof sessionBody>) =>
            (
                await api().post<CheckoutSession>(
                    '/staff/console/sessions',
                    body,
                    { idempotencyKey: key },
                )
            ).data,
    );
    const lines = useFieldArray({ control: form.control, name: 'lines' });
    const recurring = form.watch('kind') === 'recurring';
    const errors = form.formState.errors;
    const total = orderTotal(form.watch('lines'), recurring);
    const money = (
        id: string,
        name: 'trial_price' | 'confirm' | `lines.${number}.amount`,
        invalid: boolean,
    ) => (
        <MoneyInput
            id={id}
            cents={form.watch(name)}
            invalid={invalid}
            onChange={(cents) =>
                form.setValue(name, cents as number, {
                    shouldValidate: form.formState.isSubmitted,
                })
            }
        />
    );
    return (
        <>
            <Card>
                <CardHeader>
                    <CardTitle>Order</CardTitle>
                </CardHeader>
                <CardContent>
                    <form
                        method="post"
                        className="grid grid-cols-1 gap-4 md:grid-cols-2"
                        data-testid="console-form"
                        onSubmit={form.handleSubmit((values) => {
                            setError(null);
                            setOpened(null);
                            create.mutate(
                                sessionBody(
                                    merchant,
                                    values,
                                    needsConfirm,
                                    country,
                                ),
                                {
                                    onSuccess: (session) => {
                                        // the session exists now: the next submit is a new order, so it gets a new key
                                        setKey(crypto.randomUUID());
                                        if (session.url)
                                            setOpened({
                                                session: session.id,
                                                url: session.url,
                                                blocked: !openTab(session.url),
                                            });
                                        else
                                            setError(
                                                'The session has no pay page link.',
                                            );
                                    },
                                    onError: (e) => {
                                        if (!ApiError.is(e)) return;
                                        setKey(crypto.randomUUID());
                                        const mismatch = e.errors.find(
                                            (i) => i.key === MISMATCH,
                                        );
                                        // V-5: a refused customer_ref or reference shows under its own input
                                        const refused = e.errors.find(
                                            (i) =>
                                                i.field === 'customer_ref' ||
                                                i.field === 'reference',
                                        );
                                        if (mismatch) {
                                            setNeedsConfirm(true);
                                            form.setError('confirm', {
                                                type: 'server',
                                                message: mismatch.message,
                                            });
                                        } else if (refused)
                                            form.setError(
                                                refused.field as
                                                    | 'customer_ref'
                                                    | 'reference',
                                                {
                                                    type: 'server',
                                                    message:
                                                        refused.message ??
                                                        apiErrorMessage(e),
                                                },
                                            );
                                        else setError(apiErrorMessage(e));
                                    },
                                },
                            );
                        })}
                    >
                        {error ? (
                            <p
                                role="alert"
                                className="text-destructive text-sm md:col-span-2"
                            >
                                {error}
                            </p>
                        ) : null}
                        {opened ? (
                            <p
                                role={opened.blocked ? 'alert' : 'status'}
                                className="text-sm break-all md:col-span-2"
                                data-testid="console-opened"
                            >
                                {opened.blocked
                                    ? 'The browser blocked the new tab. Open checkout from this link: '
                                    : 'Checkout opened in a new tab: '}
                                <a
                                    href={opened.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="underline"
                                >
                                    {opened.url}
                                </a>
                            </p>
                        ) : null}
                        <Field id="kind" label="Order type">
                            <SearchableSelect
                                id="kind"
                                label="Order type"
                                options={KINDS}
                                value={form.watch('kind')}
                                clearable={false}
                                onChange={(v) => {
                                    if (!v) return;
                                    form.setValue('kind', v as Values['kind']);
                                    // a plan is bought alone: keep the first line only, so no hidden line is sent or validated
                                    if (
                                        v === 'recurring' &&
                                        lines.fields.length > 1
                                    )
                                        lines.replace([
                                            form.getValues('lines.0'),
                                        ]);
                                }}
                                testId="console-kind"
                            />
                        </Field>
                        <div className="space-y-4 md:col-span-2">
                            {lines.fields.map((f, i) => {
                                const e = errors.lines?.[i];
                                return (
                                    <fieldset
                                        key={f.id}
                                        className="grid grid-cols-1 gap-4 md:grid-cols-[minmax(0,2fr)_minmax(0,1fr)_minmax(0,1fr)_auto] md:items-start"
                                        data-testid={`console-line-${i}`}
                                    >
                                        <legend className="mb-2 text-sm font-medium">
                                            {recurring
                                                ? 'Plan'
                                                : `Line ${i + 1}`}
                                        </legend>
                                        <Field
                                            id={`line-${i}-name`}
                                            label="Name"
                                            error={e?.name?.message}
                                        >
                                            <Input
                                                id={`line-${i}-name`}
                                                {...form.register(
                                                    `lines.${i}.name`,
                                                )}
                                            />
                                        </Field>
                                        <Field
                                            id={`line-${i}-amount`}
                                            label={
                                                recurring
                                                    ? 'Amount (USD)'
                                                    : 'Unit amount (USD)'
                                            }
                                            error={e?.amount?.message}
                                        >
                                            {money(
                                                `line-${i}-amount`,
                                                `lines.${i}.amount`,
                                                Boolean(e?.amount),
                                            )}
                                        </Field>
                                        {recurring ? null : (
                                            <>
                                                <Field
                                                    id={`line-${i}-quantity`}
                                                    label="Quantity"
                                                    error={e?.quantity?.message}
                                                >
                                                    <Input
                                                        id={`line-${i}-quantity`}
                                                        inputMode="numeric"
                                                        {...form.register(
                                                            `lines.${i}.quantity`,
                                                        )}
                                                    />
                                                </Field>
                                                <div className="md:pt-6">
                                                    <Button
                                                        type="button"
                                                        variant="secondary"
                                                        aria-label={`Remove line ${i + 1}`}
                                                        disabled={
                                                            lines.fields
                                                                .length === 1
                                                        }
                                                        onClick={() =>
                                                            lines.remove(i)
                                                        }
                                                    >
                                                        Remove line
                                                    </Button>
                                                </div>
                                            </>
                                        )}
                                    </fieldset>
                                );
                            })}
                            {recurring ? null : (
                                <Button
                                    type="button"
                                    variant="secondary"
                                    onClick={() =>
                                        lines.append({ ...NEW_LINE })
                                    }
                                >
                                    Add line
                                </Button>
                            )}
                            <p
                                className="text-sm font-medium"
                                data-testid="console-total"
                            >
                                Order total:{' '}
                                <span className="tabular-nums">
                                    {formatMoney(total)}
                                </span>
                            </p>
                        </div>
                        {recurring ? (
                            <>
                                <Field id="interval" label="Interval">
                                    <SearchableSelect
                                        id="interval"
                                        label="Interval"
                                        options={INTERVALS}
                                        value={form.watch('interval')}
                                        clearable={false}
                                        onChange={(v) =>
                                            v &&
                                            form.setValue(
                                                'interval',
                                                v as Values['interval'],
                                            )
                                        }
                                        testId="console-interval"
                                    />
                                </Field>
                                <Field
                                    id="trial_days"
                                    label="Trial days"
                                    error={errors.trial_days?.message}
                                >
                                    <Input
                                        id="trial_days"
                                        inputMode="numeric"
                                        {...form.register('trial_days')}
                                    />
                                </Field>
                                <Field
                                    id="trial_price"
                                    label="Trial price (USD)"
                                    error={errors.trial_price?.message}
                                >
                                    {money(
                                        'trial_price',
                                        'trial_price',
                                        Boolean(errors.trial_price),
                                    )}
                                </Field>
                            </>
                        ) : (
                            <label className="flex items-center gap-2 text-sm md:col-span-2">
                                <Checkbox
                                    checked={form.watch('save_card')}
                                    onCheckedChange={(on) =>
                                        form.setValue('save_card', on === true)
                                    }
                                />
                                Offer to save the card
                            </label>
                        )}
                        {needsConfirm ? (
                            <Field
                                id="confirm"
                                label="Confirm the amount (USD)"
                                hint="Type the order total again: every line's unit amount times its quantity, added up, before tax and trial."
                                error={errors.confirm?.message}
                            >
                                {money(
                                    'confirm',
                                    'confirm',
                                    Boolean(errors.confirm),
                                )}
                            </Field>
                        ) : null}
                        <Field
                            id="customer_ref"
                            label="Customer ref"
                            hint="Optional: left blank, the API generates a console- reference."
                            error={errors.customer_ref?.message}
                        >
                            <Input
                                id="customer_ref"
                                {...form.register('customer_ref')}
                            />
                        </Field>
                        <Field
                            id="reference"
                            label="Order reference"
                            hint="Optional: the Merchant's own order reference."
                            error={errors.reference?.message}
                        >
                            <Input
                                id="reference"
                                {...form.register('reference')}
                            />
                        </Field>
                        <div className="md:col-span-2">
                            <Field
                                id="reason"
                                label="Reason"
                                error={errors.reason?.message}
                            >
                                <Textarea
                                    id="reason"
                                    rows={2}
                                    {...form.register('reason')}
                                />
                            </Field>
                        </div>
                        <div className="md:col-span-2">
                            <Button type="submit" disabled={create.isPending}>
                                Open checkout
                            </Button>
                        </div>
                    </form>
                </CardContent>
            </Card>
            {/* the pay page runs in its own tab, so this tab follows the session itself (REQ-52) */}
            {opened ? (
                <ConsoleResult
                    key={opened.session}
                    sessionId={opened.session}
                />
            ) : null}
        </>
    );
}

// the buyer is still on the pay page, or the payment webhooks have not landed yet
const WAITING: ConsoleSessionResult['state'][] = [
    'open',
    'quoted',
    'paying',
    'pending',
];
const POLL_MS = 3000;

/** Settled: the buyer finished and, for a paid session, its order exists. Until then the console reads it again. */
function settled(r: ConsoleSessionResult | undefined): boolean {
    if (!r || WAITING.includes(r.state)) return false;
    return r.state !== 'paid' || r.order_id !== null;
}

/**
 * After the pay page: the session and the charge, order and subscription it led to, as plain text (no links, § 13).
 * Read again every few seconds until the session settles, both in the console tab and on the `?session=` return.
 */
function ConsoleResult({ sessionId }: { sessionId: string }) {
    // ponytail: polls while the tab is visible until settled (an abandoned open session polls until the tab closes); add a cap if that load matters
    const query = useFreshQuery<ConsoleSessionResult>({
        ...browserQuery<ConsoleSessionResult>(
            oneSpec(`/staff/console/sessions/${encodeURIComponent(sessionId)}`),
        ),
        refetchInterval: (q) => (settled(q.state.data?.data) ? false : POLL_MS),
    });
    const r = query.data?.data;
    const rows: [string, string, string | null | undefined][] = [
        ['session', 'Session', r?.id ?? sessionId],
        ['reference', 'Order reference', r?.reference],
        ['charge', 'Charge', r?.charge_id],
        ['order', 'Order', r?.order_id],
        ['subscription', 'Subscription', r?.subscription_id],
    ];
    return (
        <Card>
            <CardHeader>
                <CardTitle>Result</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
                {query.isError ? (
                    <p role="alert" className="text-destructive text-sm">
                        The session did not load: {apiErrorMessage(query.error)}
                    </p>
                ) : null}
                <dl
                    className="grid grid-cols-1 gap-2 text-sm md:grid-cols-[auto_1fr] md:gap-x-6"
                    data-testid="console-result"
                >
                    <dt className="text-muted-foreground">State</dt>
                    <dd data-testid="result-state">
                        {r ? codeLabel(r.state) : '…'}
                    </dd>
                    {rows.map(([testId, label, value]) => (
                        <div key={testId} className="contents">
                            <dt className="text-muted-foreground">{label}</dt>
                            <dd
                                className="font-mono break-all"
                                data-testid={`result-${testId}`}
                            >
                                {r ? (value ?? 'None') : '…'}
                            </dd>
                        </div>
                    ))}
                </dl>
                {/* the charge and order land after the payment webhooks, so the ids can be read again */}
                <Button
                    variant="secondary"
                    disabled={query.isFetching}
                    onClick={() => void query.refetch()}
                >
                    Refresh
                </Button>
            </CardContent>
        </Card>
    );
}
